import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AuthCredentials } from '../../domain/models/auth-credentials.model';
import { AuthSession } from '../../domain/models/auth-session.model';
import { AuthenticatedUser } from '../../domain/models/authenticated-user.model';
import { TokenInfo } from '../../domain/models/token-info.model';
import { IAuthRepository } from '../../domain/repositories/auth.repository';
import { AuthRemoteDatasource } from '../datasources/remote/auth.remote-datasource';
import { AuthMapper } from '../mappers/auth.mapper';

@Injectable()
export class AuthRepositoryImpl implements IAuthRepository {
  private readonly datasource = inject(AuthRemoteDatasource);
  private readonly mapper = inject(AuthMapper);

  public login(credentials: AuthCredentials): Observable<AuthSession> {
    return this.datasource.login(credentials).pipe(map(dto => this.mapper.toSession(dto)));
  }

  public refresh(refreshToken: string): Observable<AuthSession> {
    return this.datasource.refresh(refreshToken).pipe(map(dto => this.mapper.toSession(dto)));
  }

  public logout(refreshToken: string): Observable<void> {
    return this.datasource.logout(refreshToken);
  }

  public getProfile(): Observable<AuthenticatedUser> {
    return this.datasource.getProfile().pipe(map(dto => this.mapper.toUser(dto)));
  }

  public updateAvatar(file: File): Observable<AuthenticatedUser> {
    return this.datasource.updateAvatar(file).pipe(map(dto => this.mapper.toUser(dto)));
  }

  public useProviderAvatar(): Observable<AuthenticatedUser> {
    return this.datasource.useProviderAvatar().pipe(map(dto => this.mapper.toUser(dto)));
  }

  public deleteAvatar(): Observable<AuthenticatedUser> {
    return this.datasource.deleteAvatar().pipe(map(dto => this.mapper.toUser(dto)));
  }

  public forgotPassword(email: string): Observable<void> {
    return this.datasource.forgotPassword(email);
  }

  public resetPassword(token: string, password: string, passwordConfirmation: string): Observable<void> {
    return this.datasource.resetPassword(token, password, passwordConfirmation);
  }

  public getResetPasswordTokenInfo(token: string): Observable<TokenInfo> {
    return this.datasource.getResetPasswordTokenInfo(token).pipe(map(dto => this.mapper.toTokenInfo(dto)));
  }

  public getInvitationCodeInfo(code: string): Observable<TokenInfo> {
    return this.datasource.getInvitationCodeInfo(code).pipe(map(dto => this.mapper.toTokenInfo(dto)));
  }

  public redeemInvitationCode(
    code: string, password: string, passwordConfirmation: string,
    acceptedLegalTermsVersion: number, acceptsMarketing: boolean,
    adultDeclarationConfirmed?: boolean, adultDeclarationVersion?: string,
  ): Observable<void> {
    return this.datasource.redeemInvitationCode(
      code, password, passwordConfirmation, acceptedLegalTermsVersion, acceptsMarketing,
      adultDeclarationConfirmed, adultDeclarationVersion,
    );
  }

  public getClinicOwnerRegistrationTokenInfo(token: string): Observable<TokenInfo> {
    return this.datasource.getClinicOwnerRegistrationTokenInfo(token).pipe(map(dto => this.mapper.toTokenInfo(dto)));
  }

  public completeClinicOwnerRegistration(
    token: string, password: string, passwordConfirmation: string, firstName: string, lastName: string,
  ): Observable<void> {
    return this.datasource.completeClinicOwnerRegistration(token, password, passwordConfirmation, firstName, lastName);
  }
}

